import { aiCreditErrorMessage } from '../../ai-chat/models/aiCreditSignals';
import axios from "axios";
import { AI_MEDIA_ANALYZE_API } from "../../../components/ai/constants";
import { API_DOMAIN } from "../../../components/constants";
import { fetchCanonicalAttachmentBlob } from "../../../components/messaging/canonicalMessagingApi";

const text = value => typeof value === "string" && value.trim() ? value.trim() : null;
export function parseAnalysis(json = {}) {
  let structured = json.structured_result;
  if (typeof structured === "string") {
    try { structured = JSON.parse(structured); } catch { structured = null; }
  }
  if (!structured || typeof structured !== "object" || Array.isArray(structured)) structured = {};
  const confidence = json.confidence_score;
  const status = text(json.status);
  return {
    status: ["completed", "failed", "pending"].includes(status) ? status : "pending",
    extractedText: text(json.extracted_text), description: text(json.description),
    confidence: (typeof confidence === "number" || (typeof confidence === "string" && confidence.trim())) && Number.isFinite(Number(confidence)) ? Math.round(Number(confidence)) : null,
    language: text(structured.detected_language),
    warnings: Array.isArray(structured.warnings) ? structured.warnings.map(text).filter(Boolean) : [],
    errorMessage: text(json.error_message),
  };
}
const config = () => ({ headers: { accessToken: localStorage.getItem("accessToken") } });
function result(response) {
  if (response.data?.success === false || !response.data?.data) {
    const error = new Error(response.data?.error?.message || response.data?.message || "The analysis failed.");
    error.response = response;
    throw error;
  }
  return parseAnalysis(response.data.data);
}
export const mediaAnalysisApi = {
  async savedBatch(messageIds) {
    const response = await axios.post(`${API_DOMAIN}/ai/media-analyses/by-messages`, { wati_message_ids: messageIds }, config());
    if (response.data?.success === false || !Array.isArray(response.data?.data?.items)) {
      throw new Error(response.data?.message || "Couldn't read saved analyses.");
    }
    return Object.fromEntries(response.data.data.items.map(item => [item.wati_message_id, parseAnalysis(item)]));
  },
  async analyse({ messageId, mediaUrl, conversationId, customerPhone, again = false }) {
    return result(await axios.post(AI_MEDIA_ANALYZE_API, {
      wati_message_id: messageId, media_url: mediaUrl,
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(customerPhone ? { customer_phone: customerPhone } : {}), force_reanalyze: again,
    }, { ...config(), timeout: 180000 }));
  },
};
export async function photoDataUrl(attachment) {
  try {
    const bytes = await fetchCanonicalAttachmentBlob(attachment.id);
    const photo = new Blob([bytes], { type: attachment.mime_type || "image/jpeg" });
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.onabort = () => reject(new Error("Photo read aborted"));
      reader.readAsDataURL(photo);
    });
  } catch (error) {
    throw Object.assign(new Error("Couldn't load the photo to analyse it."), { photoLoadFailure: true });
  }
}
export function errorCopy(error) {
  const creditMessage = aiCreditErrorMessage(error); if (creditMessage) return creditMessage;
  if (error.photoLoadFailure) return "Couldn't load the photo to analyse it.";
  const special = {
    409: "This photo is already being analysed. Try again shortly.",
    503: "AI photo analysis is turned off.", 413: "This photo is too large to analyse.",
  };
  if (special[error.response?.status]) return special[error.response.status];
  if (["ECONNABORTED", "ETIMEDOUT"].includes(error.code)) return "The analysis took too long. Try again.";
  if (error.code === "ERR_NETWORK" || (error.request && !error.response)) return "No internet connection. Check your network and try again.";
  return error.response?.data?.error?.message || error.response?.data?.message || "The analysis failed.";
}
